import { UserAvatar } from '@/components'
import { useBrowserQuery, useLocalStorage } from '@/hooks/common'
import colors from '@/styles/colors'
import { useConnectionDecline, useGetBasicInfo, useGetTopics } from '@/hooks/api/dashboard'
import { ChevronLeft } from '@mui/icons-material'
import { Box, Button, CircularProgress, Divider, Typography } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { UserDetailsContent } from '../__styled'
import dayjs from 'dayjs'
import UserTopics from './UserTopics'
import UserConnections from './Connections'
import useAppStore from '@/store/useAppStore'
import { shallow } from 'zustand/shallow'
import { toast } from 'react-hot-toast'
import { RoundedActionButton, RoundedFilledButton } from '@/components/StyledComponents'
import { ScrollableCapsulesTabBar } from '@/components/ScrollableCapsulesTabBar'
import { RBox } from '@/pages/__styled'
import { PrivacyType, PrivacyTypes, TopicType, TopicTypes } from '@/utils/constants'
import TopicItemsGrid from '@/pages/home/molecules/TopicItemsGrid'
import { ConnectionModel, ConnListAction, SearchType, Topic } from '@/types'
import { useTopicsListViewModel } from '@/hooks/api/dashboard/viewModels/useTopicsListViewModel'
import { useConnectionsListViewModel } from '@/hooks/api/dashboard/viewModels/useConnectionsListViewModel'
import { ConnectionsListContext } from '@/context/ConnectionsListContext'
import NoDataPlaceholder from '@/components/NoDataPlaceholder'
import { SVGS } from '@/assets/svgs'
import CirclesSelectDrawer from './molecules/CirclesSelectDrawer'
import { convertEgoBasicInfoToConnectionModel } from '@/utils/helper'

export default function UserDetails() {
  const [selectedTabIndex, setSelectedTabIndex] = useState(0)
  const [ewUser] = useLocalStorage('ewUser', null)
  const [selectedUserForDrawer, setSelectedUserForDrawer] = useState<ConnectionModel | null>(null)

  const topicsViewModel = useTopicsListViewModel()
  const { topicsList, loading, canLoadNextPage, loadingNextPage, loadNextPageData, getTopics } =
    topicsViewModel

  const connectionsViewModel = useConnectionsListViewModel()
  const {
    loading: loadingConnections,
    loadingNextPage: loadingConnNextPage,
    canLoadNextPage: canLoadConnNextPage,
    connections,
    getConnectionsList,
    loadNextPageData: loadNextPageConnData,
    pagination: connPagination,
  } = connectionsViewModel

  const navigate = useNavigate()
  const query = useBrowserQuery()
  const { pathname } = useLocation()
  const userId = query.get('id')
  const getUserInfo = useGetBasicInfo(userId || '')
  const declineRequest = useConnectionDecline()
  const [selectedUserProfile] = useAppStore((state) => [state.user.selectedUserProfile], shallow)
  const topicTypesToShow = [TopicType.PATTERN, TopicType.QUILT, TopicType.GALLERY, TopicType.ALBUM]
  const connectionsTabSelected = selectedTabIndex == 4

  useEffect(() => {
    if (userId) {
      getUserInfo.mutate({ isUserProfile: true })
      //selectedTabIndex(0)
    }
  }, [userId])

  useEffect(() => {
    if (getUserInfo.isSuccess) {
      loadUserTopics()
      loadUserConnections()
    }
  }, [getUserInfo.isSuccess])

  const loadUserTopics = (pageNo: number = 1) => {
    if (userId) {
      getTopics({
        pageNo: pageNo,
        privacyType: PrivacyType.ALL,
        topicType: TopicType.ALL,
        searchType: SearchType.OtherUser,
        connID: userId,
      })
    }
  }

  const loadUserConnections = (pageNo: number = 1) => {
    if (userId) {
      getConnectionsList({ connID: userId })
    }
  }
  const selectedTopicType = useMemo(() => {
    let type = TopicType.ALL
    if (selectedTabIndex < topicTypesToShow.length) {
      type = topicTypesToShow[selectedTabIndex]
    }
    return type
  }, [selectedTabIndex])

  const topics = useMemo(() => {
    if (topicsList) {
      let list = topicsList
      list = list.filter((i) => {
        const topicData = TopicTypes[Number(i.topicType)]
        const privacyData = PrivacyTypes[Number(i.visibility)]
        return (
          topicData &&
          privacyData &&
          (selectedTopicType == TopicType.ALL ? true : i.topicType == selectedTopicType.toString())
        )
      })
      // sort by date updated
      list.sort((a, b) => {
        if (a.dateUpdated > b.dateUpdated) return -1
        if (a.dateUpdated < b.dateUpdated) return 1
        return 0
      })
      return list
    }
    return []
  }, [topicsList, selectedTabIndex])

  const tabTitles = useMemo(() => {
    let mapped = topicTypesToShow.map((e, i) => {
      if (topicsList && topicsList.length > 0) {
        let filtered = topicsList.filter((j) => j.topicType === e.toString())
        return `${TopicTypes[e].tabTitle} (${filtered.length})`
      } else {
        return `${TopicTypes[e].tabTitle} (0)`
      }
    })

    mapped.push(`Followers (${connections?.length || 0})`)

    return mapped
  }, [topicsList, connections])

  const onClickTopic = (topic: Topic) => {
    const { topicID, topicType } = topic

    let previewQuery = new URLSearchParams()
    previewQuery.set('topicID', topicID)
    previewQuery.set('topicType', topicType)
    navigate(`/preview?${previewQuery.toString()}`)
  }

  if (getUserInfo.isLoading) {
    return (
      <UserDetailsContent
        justifyContent={'center'}
        alignItems='center'
        sx={{
          background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '16px',
          }}
        >
          <CircularProgress
            size={48}
            thickness={4}
            sx={{
              color: '#6366f1',
            }}
          />
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: 500,
              color: '#64748b',
            }}
          >
            Loading profile...
          </Typography>
        </Box>
      </UserDetailsContent>
    )
  }

  if (getUserInfo.isError) {
    return (
      <UserDetailsContent
        justifyContent={'center'}
        alignItems='center'
        sx={{
          background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '16px',
            padding: '40px',
            borderRadius: '16px',
            background: '#ffffff',
            boxShadow: '0 4px 16px rgba(15, 23, 42, 0.08)',
          }}
        >
          <Box
            sx={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              background: 'rgba(239, 68, 68, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography
              sx={{
                fontSize: '32px',
              }}
            >
              😕
            </Typography>
          </Box>
          <Typography
            sx={{
              fontSize: '18px',
              fontWeight: 600,
              color: '#0f172a',
            }}
          >
            User not found
          </Typography>
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: 400,
              color: '#64748b',
              textAlign: 'center',
            }}
          >
            The profile you're looking for doesn't exist or has been removed.
          </Typography>
        </Box>
      </UserDetailsContent>
    )
  }

  const profile = selectedUserProfile
  if (!profile) return null

  const onClickConnect = () => {
    const model = convertEgoBasicInfoToConnectionModel(profile)
    onUserAction(model, ConnListAction.FOLLOW)
  }

  const onClickAcceptRequest = () => {
    const model = convertEgoBasicInfoToConnectionModel(profile)
    onUserAction(model, ConnListAction.ACCEPT_CONN_REQUEST)
  }

  const onClickDeclineRequest = () => {
    toast.promise(declineRequest.mutateAsync(String(profile.reqSentConnReqID)), {
      loading: 'Processing the decline...',
      success: <Typography variant='p'>{`Success!`}</Typography>,
      error: <Typography variant='p'>Failed to decline the request.</Typography>,
    })
  }

  const renderConnectionStatus = () => {
    if (profile.userID === ewUser.userID || profile.isConnection) return null
    if (!profile.reqSent && !profile.reqReceived) {
      return (
        <Button
          onClick={onClickConnect}
          sx={{
            height: '44px',
            padding: '0 28px',
            borderRadius: '12px',
            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
            color: '#ffffff',
            fontSize: '14px',
            fontWeight: 600,
            textTransform: 'none',
            border: 'none',
            boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              background: 'linear-gradient(135deg, #5558e3 0%, #7c4de8 100%)',
              transform: 'translateY(-2px)',
              boxShadow: '0 6px 20px rgba(99, 102, 241, 0.4)',
            },
            '&:active': {
              transform: 'translateY(0) scale(0.98)',
            },
          }}
        >
          Follow
        </Button>
      )
    } else if (profile.reqReceived) {
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 18px',
            borderRadius: '12px',
            background: 'rgba(245, 158, 11, 0.08)',
            border: '1px solid rgba(245, 158, 11, 0.3)',
          }}
        >
          <Box
            sx={{
              width: '6px',
              height: '6px',
              borderRadius: '50%',
              background: '#f59e0b',
              animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
              '@keyframes pulse': {
                '0%, 100%': {
                  opacity: 1,
                },
                '50%': {
                  opacity: 0.5,
                },
              },
            }}
          />
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: 600,
              color: '#f59e0b',
            }}
          >
            Pending
          </Typography>
        </Box>
      )
    } else if (profile.reqSent) {
      return (
        <Box sx={{ display: 'flex', gap: '12px' }}>
          <Button
            onClick={onClickDeclineRequest}
            sx={{
              height: '44px',
              padding: '0 24px',
              borderRadius: '12px',
              background: '#f8fafc',
              color: '#ef4444',
              fontSize: '14px',
              fontWeight: 600,
              textTransform: 'none',
              border: '2px solid #fee2e2',
              boxShadow: 'none',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                background: '#fef2f2',
                borderColor: '#fecaca',
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 12px rgba(239, 68, 68, 0.15)',
              },
              '&:active': {
                transform: 'translateY(0) scale(0.98)',
              },
            }}
          >
            Reject
          </Button>
          <Button
            onClick={onClickAcceptRequest}
            sx={{
              height: '44px',
              padding: '0 28px',
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: '#ffffff',
              fontSize: '14px',
              fontWeight: 600,
              textTransform: 'none',
              border: 'none',
              boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 20px rgba(16, 185, 129, 0.4)',
              },
              '&:active': {
                transform: 'translateY(0) scale(0.98)',
              },
            }}
          >
            Accept
          </Button>
        </Box>
      )
    }
    return null
  }

  const onUserAction = (user: ConnectionModel, action: ConnListAction) => {
    if (action == ConnListAction.FOLLOW || action == ConnListAction.ACCEPT_CONN_REQUEST) {
      setSelectedUserForDrawer(user)
    }
  }

  return (
    <UserDetailsContent sx={{ width: '100%', overflow: 'visible', height: 'auto' }}>
      {/* Hero Section with Cover */}
      <Box
        sx={{
          position: 'relative',
          height: '240px',
          background: profile?.profileCoverURL
            ? `url(${profile.profileCoverURL})`
            : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(180deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.6) 100%)',
            backdropFilter: 'blur(2px)',
          },
        }}
      >
        {/* Avatar positioned at bottom of hero */}
        <Box
          sx={{
            position: 'absolute',
            bottom: '-60px',
            left: '40px',
            zIndex: 10,
          }}
        >
          <Box
            sx={{
              position: 'relative',
              width: '140px',
              height: '140px',
            }}
          >
            <UserAvatar
              name={`${profile?.firstName} ${profile?.lastName}`}
              url={profile?.profilePicURL}
              borderRadius={100}
              size={140}
            />
            <Box
              sx={{
                position: 'absolute',
                inset: '-5px',
                borderRadius: '50%',
                border: '5px solid #ffffff',
                boxShadow: '0 8px 32px rgba(99, 102, 241, 0.2)',
                zIndex: -1,
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* Profile Info Section */}
      <Box
        sx={{
          position: 'relative',
          padding: '72px 40px 32px 40px',
          background: '#ffffff',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: '24px',
          }}
        >
          {/* User Info */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: '28px',
                fontWeight: 700,
                color: '#0f172a',
                letterSpacing: '-0.02em',
                mb: '8px',
              }}
              className='username'
            >
              {profile.firstName} {profile.lastName}
            </Typography>

            {profile.motto && (
              <Typography
                className='motto'
                sx={{
                  fontSize: '15px',
                  fontWeight: 500,
                  color: '#64748b',
                  fontStyle: 'italic',
                  mb: '12px',
                }}
              >
                "{profile.motto}"
              </Typography>
            )}

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                mt: '12px',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '6px 14px',
                  borderRadius: '8px',
                  background: 'rgba(99, 102, 241, 0.08)',
                  border: '1px solid rgba(99, 102, 241, 0.2)',
                }}
              >
                <Box
                  sx={{
                    width: '6px',
                    height: '6px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                  }}
                />
                <Typography
                  sx={{
                    fontSize: '13px',
                    fontWeight: 600,
                    color: '#6366f1',
                  }}
                >
                  Member since {dayjs(profile?.joinDate).format('MMM YYYY')}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Connection Actions */}
          <Box sx={{ mt: '4px' }}>{renderConnectionStatus()}</Box>
        </Box>
      </Box>

      {/* Tabs Section */}
      <Box
        sx={{
          px: '40px',
          pt: '24px',
          pb: '16px',
          width: '100%',
          background: '#ffffff',
          borderTop: '1px solid #e2e8f0',
          zIndex: 5,
          boxShadow: '0 1px 0 0 rgba(0,0,0,0.05)',
        }}
      >
        <ScrollableCapsulesTabBar
          titles={tabTitles}
          selectedIndex={selectedTabIndex}
          onSelectAtIndex={setSelectedTabIndex}
          showScrollButtons={false}
        />
      </Box>

      {/* Content Section */}
      {(topicsList && topics.length == 0 && !connectionsTabSelected) ||
      (connections && connections.length == 0 && connectionsTabSelected) ? (
        <Box
          flexDirection={'column'}
          display={'flex'}
          sx={{
            width: '100%',
            minHeight: '400px',
            background: '#f8fafc',
            padding: '60px 40px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flex: 1,
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {!connectionsTabSelected && userId && (
              <NoDataPlaceholder
                icon={TopicTypes[selectedTopicType].outlineIcon}
                title={`No ${TopicTypes[selectedTopicType].tabTitle.toLowerCase()} created`}
                subTitle={`${profile?.firstName || ''} has not created any ${TopicTypes[
                  selectedTopicType
                ].title.toLowerCase()} ${
                  selectedTopicType == TopicType.PATTERN || selectedTopicType == TopicType.QUILT
                    ? 'design'
                    : ''
                } yet!`}
              />
            )}
            {connectionsTabSelected && userId && (
              <NoDataPlaceholder
                icon={SVGS.NoConnectionsIcon}
                title={`No Followers`}
                subTitle={`${profile?.firstName || ''} has no any follower yet!`}
              />
            )}
          </Box>
        </Box>
      ) : (
        <Box
          flexDirection={'column'}
          display={'flex'}
          sx={{
            width: '100%',
            background: '#f8fafc',
            padding: '32px 40px 60px 40px',
          }}
        >
          {!connectionsTabSelected && userId && (
            <TopicItemsGrid
              topics={topics}
              onClickTopicItem={onClickTopic}
              onRemoved={() => {}}
              filter={{ topicType: selectedTopicType, privacyType: PrivacyType.ALL }}
              showAuthor={false}
              showAuthorForPublic={false}
            />
          )}
          {connectionsTabSelected && userId && (
            <ConnectionsListContext.Provider
              value={{
                items: connections,
                loading: loadingConnections,
                loadingNextPage: loadingConnNextPage,
                canLoadNextPage: canLoadConnNextPage,
                loadNextPageData: loadNextPageConnData,
              }}
            >
              <UserConnections onUserAction={onUserAction} />
            </ConnectionsListContext.Provider>
          )}
        </Box>
      )}
      <CirclesSelectDrawer
        open={selectedUserForDrawer != null}
        user={selectedUserForDrawer}
        onClose={() => setSelectedUserForDrawer(null)}
      />
    </UserDetailsContent>
  )
}
